import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { InternalLinkRoutingDirective } from '@app/shared/directives/internal-link-routing.directive';
import { QRCodeComponent } from 'angularx-qrcode';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { PermissionService } from '../../core/services/permission.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslateMarkdownPipe } from '../../shared/pipes/translate-markdown.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Full page component for displaying QR codes for asset/interaction type associations
 * Supports download, print, and navigation to interaction window.history
 */
@Component({
  selector: 'app-qr-code',
  imports: [
    RouterLink,
    QRCodeComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    SpinnerComponent,
    HasPermissionDirective,
    TippyDirective,
    CheckpointFormHeaderComponent,
    TranslatePipe,
    TranslateMarkdownPipe,
    InternalLinkRoutingDirective,
  ],
  templateUrl: './qr-code.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './qr-code.component.css',
})
export class QrCodeComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private permissionService = inject(PermissionService);
  private logger = inject(LoggerService).getLogger('qr-code');
  private translate = inject(TranslationService);

  loading = signal(true);
  error = signal<string | null>(null);

  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);

  qrUrl = signal(''); // Full absolute URL for QR code
  routerUrl = signal(''); // Router path for in-app navigation
  title = signal('QR Code');
  description = signal('');
  linkText = signal('');

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.loading.set(true);
    this.error.set(null);

    try {
      const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
      const formId = this.route.snapshot.paramMap.get('formId');

      if (!checkPointId) {
        this.error.set(this.translate.translate('qrCode.error.missingCheckpointId'));
        this.loading.set(false);
        return;
      }

      // Load asset
      const checkPoint = await this.checkPointService.getById(checkPointId);
      if (!checkPoint) {
        this.error.set(
          this.translate.translate('qrCode.error.assetNotFound', { checkPointId: checkPointId }),
        );
        this.loading.set(false);
        return;
      }
      this.checkPoint.set(checkPoint);

      // If interaction type ID is provided, load it and create specific URL
      if (formId) {
        const form = await this.formService.getById(formId);
        if (!form) {
          this.error.set(this.translate.translate('qrCode.error.formNotFound', { formId: formId }));
          this.loading.set(false);
          return;
        }
        this.form.set(form);

        // Create URL for specific asset + interaction type (Step 4 will migrate this)
        const url = `${window.location.origin}/record/${checkPointId}/${formId}`;
        this.qrUrl.set(url);
        this.routerUrl.set(`/record/${checkPointId}/${formId}`);
        this.title.set(`${checkPoint.name} - ${form.name}`);
        this.breadcrumbService.setDynamicLabels([checkPoint.name, `${form.name} QR`]);
        this.description.set(
          this.translate.translate('qrCode.description.specific', {
            formName: form.name,
          }),
        );
        this.linkText.set(
          this.translate.translate('qrCode.link.specific', {
            formName: form.name,
          }),
        );
      } else {
        // Create URL for asset only (user will choose interaction type)
        const url = `${window.location.origin}/scan/${checkPointId}`;
        this.qrUrl.set(url);
        this.routerUrl.set(`/scan/${checkPointId}`);
        this.title.set(checkPoint.name);
        this.breadcrumbService.setDynamicLabels([checkPoint.name, 'QR Code']);
        this.description.set(this.translate.translate('qrCode.description.choose'));
        this.linkText.set(this.translate.translate('qrCode.link.choose'));
      }

      this.loading.set(false);
    } catch (err) {
      this.logger.error('Failed to load QR code data:', err);
      this.error.set(this.translate.translate('qrCode.error.loadFailed'));
      this.loading.set(false);
    }
  }

  /**
   * Navigate to interaction history page (only available when interactionType is set)
   */
  viewHistory() {
    const checkPoint = this.checkPoint();
    const form = this.form();

    if (checkPoint && form) {
      this.router.navigate(['/submission', checkPoint.id, form.id, 'history']);
    }
  }

  /**
   * Navigate to edit form page (only available when interactionType is set)
   */
  editForm() {
    const form = this.form();
    if (form) {
      this.router.navigate(['/forms', form.id]);
    }
  }

  /**
   * Download the QR code as an image
   */
  downloadQrCode() {
    const canvas = window.document.querySelector('app-qr-code canvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `qr-code-${Date.now()}.png`;
      link.click();
    }
  }

  /**
   * Print the QR code
   */
  printQrCode() {
    const canvas = window.document.querySelector('app-qr-code canvas') as HTMLCanvasElement;
    if (canvas) {
      const printTitle = this.translate.translate('qrCode.print.title');
      const urlLabel = this.translate.translate('qrCode.print.urlLabel');
      const windowContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${printTitle}</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                flex-direction: column;
                align-items: center;
                font-family: system-ui, -apple-system, sans-serif;
              }
              h1 {
                margin: 0 0 10px 0;
                font-size: 24px;
              }
              p {
                margin: 0 0 20px 0;
                color: #666;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                @page {
                  margin: 0.5in;
                }
              }
            </style>
          </head>
          <body>
            <h1>${this.title()}</h1>
            ${this.description() ? `<p>${this.description()}</p>` : ''}
            <img src="${canvas.toDataURL('image/png')}" alt="QR Code" />
            <div style="margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; max-width: 400px; word-break: break-all;">
              <div style="font-weight: 600; margin-bottom: 5px; color: #333;">${urlLabel}</div>
              <div style="font-family: 'Courier New', monospace; font-size: 12px; color: #666;">${this.qrUrl()}</div>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(windowContent);
        printWindow.document.close();
        printWindow.focus();

        // Wait for image to load before printing
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  }

  printLabels(): void {
    const cp = this.checkPoint();
    const form = this.form();
    const checkpointId = this.route.snapshot.paramMap.get('checkpointId');
    const formId = this.route.snapshot.paramMap.get('formId');

    if (checkpointId && formId) {
      this.router.navigate(['/checkpoints', checkpointId, formId, 'labels']);
    } else if (cp) {
      // Fallback: checkpoint-only QR page (no form)
      this.logger.warn('Print labels requires a form association');
    }
  }
}
